import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  StatsApi,
  toArticleRows,
  type ArticleStatsRow,
  type StatsQuery,
} from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import type { StatMetric } from '../components/statsColumns'

/**
 * Everything the article endpoint reports per article. Its `ctr` is the 0-1
 * fraction the stats API uses everywhere else, hence `ratio`.
 */
export const ARTICLE_STAT_METRICS: StatMetric[] = [
  { key: 'impressions', labelKey: 'stats.impressions', format: 'count', defaultVisible: true },
  { key: 'clicks', labelKey: 'stats.clicks', format: 'count', defaultVisible: true },
  { key: 'ctr', labelKey: 'stats.ctr', format: 'ratio', defaultVisible: true },
  { key: 'viewables', labelKey: 'stats.viewables', format: 'count', defaultVisible: true },
  { key: 'rendered', labelKey: 'stats.rendered', format: 'count' },
]

export type { ArticleStatsRow }

/**
 * The articles a line item's ads were served against, busiest first. The
 * endpoint only breaks down by article when asked to aggregate to one, and
 * `topN` keeps the list to the head of a very long tail.
 */
export function useArticleStats(
  query: Partial<StatsQuery>,
  range: DateRange,
  topN = 25,
  enabled = true
) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => getDateRange(range, timeZone), [range, timeZone])

  const result = useQuery({
    queryKey: ['article-stats', query, range, startDate, endDate, topN],
    queryFn: () =>
      StatsApi.getArticles({
        startDate,
        endDate,
        aggregateTo: 'ARTICLE',
        topN,
        timeout: 180000,
        ...query,
      }),
    enabled: enabled && hasToken && !!currentNetwork,
  })

  const rows = useMemo(() => toArticleRows(result.data), [result.data])

  return {
    rows,
    isLoading: result.isLoading,
    error: result.error as Error | null,
  }
}
